// Greek vocative inflection for in-app salutations.
//
// Direct port of /app/backend/greek_vocative.py — keep the two in sync if
// the rules ever change. The whole module covers the same edge cases:
//
//   Θεόδωρος   → Θεόδωρε   (4 syllables, -ος → -ε)
//   Νίκος      → Νίκο      (2 syllables, -ος → -ο)
//   Παναγιώτης → Παναγιώτη (-ης → drop ς)
//   Λουκάς     → Λουκά     (-ας → drop ς)
//   Μαρία      → Μαρία     (female -α → unchanged)
//   Robert     → Robert    (no Greek chars → unchanged)

const EXCEPTIONS = {
  // key = lower-cased + accent-stripped + final-σ folded
  'θεοσ':     'Θεέ',
  'δημητρισ': 'Δημήτρη',
};

const VOWELS = new Set(['α', 'ε', 'η', 'ι', 'ο', 'υ', 'ω']);

function stripAccents(s) {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function decompose(name) {
  const chars = [];
  const accents = [];
  for (const ch of (name || '').toLowerCase().normalize('NFD')) {
    const code = ch.charCodeAt(0);
    if (code >= 0x0300 && code <= 0x036f) {
      if (accents.length) accents[accents.length - 1] = true;
    } else {
      chars.push(ch);
      accents.push(false);
    }
  }
  return { chars, accents };
}

function countSyllables(name) {
  const { chars, accents } = decompose(name);
  let syll = 0;
  let inVowel = false;
  let iyGlide = false;
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (VOWELS.has(ch)) {
      if (!inVowel) {
        syll += 1;
        inVowel = true;
        iyGlide = (ch === 'ι' || ch === 'υ') && !accents[i];
      } else if (ch === 'ι' || ch === 'υ') {
        // diphthong second element (αυ, ευ, ου, αι, ει, οι)
        iyGlide = false;
      } else if (iyGlide) {
        // unaccented ι/υ + strong vowel = glide, same syllable
        iyGlide = false;
      } else {
        // hiatus → new syllable
        syll += 1;
        iyGlide = false;
      }
    } else {
      inVowel = false;
      iyGlide = false;
    }
  }
  return syll;
}

function hasGreek(s) {
  if (!s) return false;
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if ((code >= 0x0370 && code <= 0x03ff) || (code >= 0x1f00 && code <= 0x1fff)) {
      return true;
    }
  }
  return false;
}

/**
 * Convert a Greek first name from nominative → vocative case.
 *
 * Latin-script names and empty inputs pass through unchanged so callers can
 * do `\`Γεια σου ${toVocative(name)},\`` without extra guards.
 */
export function toVocative(firstName) {
  if (!firstName) return firstName || '';
  const name = firstName.trim();
  if (!name || !hasGreek(name)) return name;

  const key = stripAccents(name).replace(/ς/g, 'σ');
  if (EXCEPTIONS[key]) return EXCEPTIONS[key];

  const plain = key; // already accent-stripped + ς→σ

  // -ης → drop ς (Παναγιώτης → Παναγιώτη)
  if (plain.endsWith('ησ')) return name.slice(0, -1);
  // -ας → drop ς (Λουκάς → Λουκά)
  if (plain.endsWith('ασ')) return name.slice(0, -1);
  // -ις / -υς → drop ς (Άρις → Άρι)
  if (plain.endsWith('ισ') || plain.endsWith('υσ')) return name.slice(0, -1);
  // -ος → -ε for 3+ syllables, -ο for 2-syllable diminutives
  if (plain.endsWith('οσ')) {
    return name.slice(0, -2) + (countSyllables(name) >= 3 ? 'ε' : 'ο');
  }
  // Female endings (-α / -η / -ω / -ού) and anything else → unchanged
  return name;
}

/**
 * Best-effort grammatical gender from a Greek first name, for adjective
 * agreement (e.g. έτοιμος vs έτοιμη). Returns 'm' | 'f' | null (unknown /
 * Latin name). Heuristic by name ending — matches the vocative rules above.
 */
export function greekGender(firstName) {
  const name = (firstName || '').trim();
  if (!name || !hasGreek(name)) return null;
  const k = stripAccents(name).replace(/ς/g, 'σ');
  // Male endings: -ος, -ης, -ας, -ις, -υς
  if (k.endsWith('οσ') || k.endsWith('ησ') || k.endsWith('ασ') || k.endsWith('ισ') || k.endsWith('υσ')) return 'm';
  // Female endings: -α, -η, -ω
  if (k.endsWith('α') || k.endsWith('η') || k.endsWith('ω')) return 'f';
  return null;
}

export default toVocative;
